import type { CombatAbilityData, CombatAbilityMetrics, CombatKitBalance, GameplayOutcome } from './types';

type AbilityLike = Pick<CombatAbilityData,
  'form'|'cooldown'|'areaDurationSeconds'|'activationMode'|'holdRepeatIntervalSeconds'|
  'channelDurationSeconds'|'channelTickIntervalSeconds'|'castTimeSeconds'|'criticalMode'|'outcomes'>;

const round = (value: number) => Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;
const positive = (value: number | null | undefined) => Math.max(0, Number(value ?? 0));
const periodicTicks = (duration: number) => Math.max(1, Math.floor(positive(duration)));
const repeatedTicks = (duration: number, interval: number) => {
  const safeDuration = positive(duration);
  const safeInterval = Math.max(0.05, positive(interval) || 1);
  return Math.max(1, Math.floor((safeDuration + 1e-6) / safeInterval));
};

function outcomeTotals(outcome: GameplayOutcome): { damage: number; healing: number; duration: number; note?: string } {
  const base = positive(outcome.baseValue);
  const percent = outcome.valueCalculation !== 'flat';
  const note = percent ? 'Prozentbasierte HP-Wirkungen sind ohne Referenz-HP nicht in den Zahlen enthalten.' : undefined;
  if (percent) return { damage: 0, healing: 0, duration: positive(outcome.durationSeconds), note };
  if (outcome.type === 'damage') return { damage: base, healing: 0, duration: 0 };
  if (outcome.type === 'healing') return { damage: 0, healing: base, duration: 0 };
  if (outcome.type === 'damage_over_time') return { damage: base * periodicTicks(positive(outcome.durationSeconds)), healing: 0, duration: positive(outcome.durationSeconds) };
  if (outcome.type === 'healing_over_time') return { damage: 0, healing: base * periodicTicks(positive(outcome.durationSeconds)), duration: positive(outcome.durationSeconds) };
  return { damage: 0, healing: 0, duration: positive(outcome.durationSeconds) };
}

export function estimateAbilityMetrics(ability: AbilityLike): CombatAbilityMetrics {
  const notes = new Set<string>();
  let damage = 0;
  let healing = 0;
  let longestOutcome = 0;
  for (const outcome of ability.outcomes) {
    const total = outcomeTotals(outcome);
    damage += total.damage;
    healing += total.healing;
    longestOutcome = Math.max(longestOutcome, total.duration);
    if (total.note) notes.add(total.note);
  }

  const isChannel = ability.activationMode === 'channel';
  const channelDuration = isChannel ? positive(ability.channelDurationSeconds) : 0;
  const channelInterval = positive(ability.channelTickIntervalSeconds) || 1;
  const areaDuration = ability.form === 'persistent_area' ? positive(ability.areaDurationSeconds) : 0;
  const repeatedDuration = isChannel ? channelDuration : areaDuration;
  const repeatCount = repeatedDuration > 0 ? repeatedTicks(repeatedDuration, isChannel ? channelInterval : 1) : 1;

  // A persistent area or channel repeats ordinary direct outcomes. Periodic outcomes already contain their own tick total.
  if (repeatCount > 1) {
    const repeatableDamage = ability.outcomes.filter((outcome) => outcome.type === 'damage').reduce((sum, outcome) => sum + positive(outcome.baseValue), 0);
    const repeatableHealing = ability.outcomes.filter((outcome) => outcome.type === 'healing' && outcome.valueCalculation === 'flat').reduce((sum, outcome) => sum + positive(outcome.baseValue), 0);
    damage += repeatableDamage * (repeatCount - 1);
    healing += repeatableHealing * (repeatCount - 1);
  }

  const executionDuration = positive(ability.castTimeSeconds) + channelDuration;
  const cycleSeconds = Math.max(0.05, executionDuration + positive(ability.cooldown));
  const estimatedDuration = Math.max(executionDuration, areaDuration, longestOutcome);
  const criticalMaximum = ability.criticalMode === 'none' ? damage : damage * 2;
  if (ability.criticalMode !== 'none') notes.add('DPS/HPS basiert auf normalen Treffern; kritischer Maximalschaden wird separat mit Standardfaktor 2,0 angezeigt.');
  notes.add('Berechnung ohne Charakterwerte, Resistenzen, verfehlte Treffer und Unreal-Animations-/Triggerzeiten.');
  if (ability.activationMode === 'hold_repeat') notes.add('Die Einzelwert-Anzeige gilt pro Ausführung; Kit-Balancing berücksichtigt die vollständige Combo-Kette und den Halteabstand.');

  return {
    estimatedDamage: round(damage),
    estimatedHealing: round(healing),
    estimatedDurationSeconds: round(estimatedDuration),
    estimatedDps: round(damage / cycleSeconds),
    estimatedHps: round(healing / cycleSeconds),
    criticalDamageMaximum: round(criticalMaximum),
    notes: [...notes],
  };
}

export function estimateComboMetrics(stages: AbilityLike[]): CombatAbilityMetrics {
  if (stages.length === 0) return { estimatedDamage:0,estimatedHealing:0,estimatedDurationSeconds:0,estimatedDps:0,estimatedHps:0,criticalDamageMaximum:0,notes:[] };
  const stageMetrics = stages.map(estimateAbilityMetrics);
  const root = stages[0];
  const repeatInterval = root.activationMode === 'hold_repeat' ? positive(root.holdRepeatIntervalSeconds) : 0;
  let executionSeconds = 0;
  stages.forEach((stage, index) => {
    const stageSeconds = positive(stage.castTimeSeconds) + (stage.activationMode === 'channel' ? positive(stage.channelDurationSeconds) : 0);
    const earliestStart = repeatInterval > 0 ? index * repeatInterval : executionSeconds;
    executionSeconds = Math.max(executionSeconds, earliestStart) + stageSeconds;
  });
  const cycleSeconds = Math.max(0.05, executionSeconds + positive(root.cooldown));
  const damage = stageMetrics.reduce((sum, value) => sum + value.estimatedDamage, 0);
  const healing = stageMetrics.reduce((sum, value) => sum + value.estimatedHealing, 0);
  return {
    estimatedDamage: round(damage),
    estimatedHealing: round(healing),
    estimatedDurationSeconds: round(Math.max(executionSeconds, ...stageMetrics.map((value) => value.estimatedDurationSeconds))),
    estimatedDps: round(damage / cycleSeconds),
    estimatedHps: round(healing / cycleSeconds),
    criticalDamageMaximum: round(stageMetrics.reduce((sum, value) => sum + value.criticalDamageMaximum, 0)),
    notes: [...new Set(stageMetrics.flatMap((value) => value.notes))],
  };
}

export function estimateKitBalance(slots: { light?: AbilityLike[]; heavy?: AbilityLike[]; ultimate?: AbilityLike[] }): CombatKitBalance {
  const light = estimateComboMetrics(slots.light ?? []);
  const heavy = estimateComboMetrics(slots.heavy ?? []);
  const ultimate = estimateComboMetrics(slots.ultimate ?? []);
  return {
    theoreticalDps: round(light.estimatedDps + heavy.estimatedDps + ultimate.estimatedDps),
    theoreticalHps: round(light.estimatedHps + heavy.estimatedHps + ultimate.estimatedHps),
    lightDps: light.estimatedDps,
    heavyDps: heavy.estimatedDps,
    ultimateDps: ultimate.estimatedDps,
    lightHps: light.estimatedHps,
    heavyHps: heavy.estimatedHps,
    ultimateHps: ultimate.estimatedHps,
    notes: ['Theoretischer 100-%-Uptime-Wert: Light, Heavy und Ultimate werden als vollständig ausgelastete Einzelzyklen addiert.', ...new Set([...light.notes,...heavy.notes,...ultimate.notes])],
  };
}
